import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { environment } from 'environments/environment';
import { FuseConfigService } from '@fuse/services/config';
import { lastValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
    
    constructor(
        private _http: HttpClient,
        private _config: FuseConfigService,
        private _fuseConfigService: FuseConfigService
    ) {}

    /**
     * Initialize push notifications
     * Call this after user logs in
     */
    async initialize(): Promise<void> {
        console.log('Initializing push notifications...');
        
        if (Capacitor.isNativePlatform()) {
            await this.initNative();
        } else {
            await this.initWeb();
        }
    }

    /**
     * Initialize for iOS/Android
     */
    private async initNative(): Promise<void> {
        console.log('Initializing native push notifications...');
        
        // Request permission
        let permStatus = await PushNotifications.checkPermissions();
        
        if (permStatus.receive === 'prompt') {
            permStatus = await PushNotifications.requestPermissions();
        }
        
        if (permStatus.receive !== 'granted') {
            console.log('Push notification permission not granted');
            return;
        }

        // Register with FCM
        await PushNotifications.register();

        // Handle successful registration
        await PushNotifications.addListener('registration', (token: Token) => {
            console.log('Push registration success, token:', token.value);
            this.saveToken(token.value, Capacitor.getPlatform());
        });

        // Handle registration errors
        await PushNotifications.addListener('registrationError', (error: any) => {
            console.error('Push registration error:', error);
        });

        // Handle notification received while app is open
        await PushNotifications.addListener('pushNotificationReceived', 
            (notification: PushNotificationSchema) => {
                console.log('Push notification received:', notification);
                // You can show a custom in-app notification here if desired
            }
        );

        // Handle notification tap (user clicked on notification)
        await PushNotifications.addListener('pushNotificationActionPerformed', 
            (action: ActionPerformed) => {
                console.log('Push notification action performed:', action);
                
                // Handle navigation based on notification data
                const data = action.notification.data;
                if (data?.type) {
                    this.handleNotificationNavigation(data);
                }
            }
        );
    }

    /**
     * Initialize for web browsers
     * DISABLED: Web push notifications temporarily disabled on dev branch
     */
    private async initWeb(): Promise<void> {
        console.log('[Push] Web push notifications disabled on dev branch');
        return;
        
        // // Initialize Firebase
        // const app = initializeApp(this._config.global.settings["firebase"]);
        // const messaging = getMessaging(app);

        // // Request permission
        // const permission = await Notification.requestPermission();
        
        // if (permission !== 'granted') {
        //     console.log('Web notification permission not granted');
        //     return;
        // }

        // // Get FCM token
        // try {
        //     const token = await getToken(messaging, {
        //         vapidKey: this._config.global.settings["firebase"]["vapidKey"]
        //     });
            
        //     if (token) {
        //         console.log('FCM Token obtained:', token);
        //         await this.saveToken(token, 'web');
        //     } else {
        //         console.log('No FCM token available');
        //     }
        // } catch (err) {
        //     console.error('Error getting FCM token:', err);
        // }

        // // Handle messages when app is in foreground
        // onMessage(messaging, (payload) => {
        //     console.log('Message received in foreground:', payload);
            
        //     // Show browser notification
        //     if (payload.notification) {
        //         new Notification(payload.notification.title, {
        //             body: payload.notification.body,
        //             icon: '/assets/icons/icon-192x192.png',
        //             data: payload.data
        //         });
        //     }
        // });
    }

    /**
     * Save device token to backend
     */
    private async saveToken(token: string, platform: string): Promise<void> {
        try {
            const userType = "individual"
            const payload = {
                "deviceToken": {
                    token,
                    platform
                }
            }
            try {
                await lastValueFrom(this._http.post(this._fuseConfigService.global.settings["api_url"] + `${userType}/internal/user-management/save-token`, payload))
            } catch (error) {
                
            }
        } catch (error) {
            console.error('Error saving device token:', error);
        }
    }

    /**
     * Delete device token from backend database only
     * Call this when user signs out
     * NOTE: This keeps the token in local storage so it can be reassigned to the next user
     * who signs in on this device - acts as a cross-account token sharing system
     */
    public async deleteToken(): Promise<void> {
        // TODO(demo): legacy `delete-token` (device push-token revoke) has no `/v1`
        // equivalent, and web push itself is disabled on this branch (see `initWeb()`'s
        // own comment) — no-op rather than firing against a route the new Worker
        // doesn't have.
        return;
    }

    /**
     * Handle navigation when notification is tapped
     */
    private handleNotificationNavigation(data: any): void {
        // Add your navigation logic here based on notification type
        // Example:
        // if (data.type === 'Notification.NewReflection' && data.ref_conversation) {
        //     this._router.navigate(['/member/reflections', data.ref_conversation]);
        // }
    }
}